// Month-to-date water saved vs. a counterfactual baseline. Synthetic per
// ADR-0002 — flagged with "synthetic" text so demo viewers don't mistake it
// for billed usage.

import { useEffect, useState } from 'react';
import { Droplets } from 'lucide-react';
import { DataCard } from './ui/data-card';
import { getWaterSaved, type WaterSavedResponse } from '../api/overview.js';

interface Props {
  courseSlug: string;
}

function formatGallons(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000).toLocaleString()}k`;
  return n.toLocaleString();
}

function formatUSD(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

export function WaterSavedCard({ courseSlug }: Props) {
  const [data, setData] = useState<WaterSavedResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await getWaterSaved(courseSlug);
        if (!cancelled) setData(resp);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug]);

  if (error) {
    return (
      <DataCard title="Water saved · MTD">
        <div className="text-xs text-fg-muted">{error}</div>
      </DataCard>
    );
  }
  if (!data) {
    return (
      <DataCard title="Water saved · MTD">
        <div className="text-sm text-fg-muted">Loading…</div>
      </DataCard>
    );
  }

  const savedPct =
    data.baselineGallons > 0
      ? Math.round(((data.baselineGallons - data.actualGallons) / data.baselineGallons) * 100)
      : 0;

  return (
    <DataCard
      title="Water saved · MTD"
      subtitle="vs. without-GreenIQ baseline · synthetic"
      actions={
        <div className="flex items-center gap-1 text-xs text-greeniq-400">
          <Droplets size={12} />
          −{savedPct}%
        </div>
      }
    >
      <div className="flex items-end justify-between gap-6">
        <div>
          <div className="flex items-baseline gap-2">
            <div className="font-display text-4xl font-semibold text-greeniq-400">
              {formatGallons(data.savedGallons)}
            </div>
            <div className="text-xs uppercase tracking-wide text-fg-muted">gal</div>
          </div>
          <div className="mt-1 text-xs text-fg-muted">
            {formatUSD(data.dollarsSaved)} at current water rate
          </div>
        </div>
        <div className="text-right text-[11px] text-fg-muted">
          <div>Actual: {formatGallons(data.actualGallons)} gal</div>
          <div>Baseline: {formatGallons(data.baselineGallons)} gal</div>
        </div>
      </div>
    </DataCard>
  );
}
